import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {getConsolidatedStockList, getSubset} from "./model";
import Preview from "./Preview/Preview";
import {Tag} from "antd";
import {WarningOutlined} from "@ant-design/icons";
import AggregateView from "./AggregateView/AggregateView";
import {selectAppDataByType} from "../../Store/slices/user.slice";
import TickerSearch from "../../Components/TickerSearch/TickerSearch";
import PageHeader from "../../Components/PageHeader";
import {usePortfolio} from "../../Contexts/PortfolioContext";
import {iconMapping} from "../../Configs/AppsConfig";
import {filterInactiveHoldings, fetchAndProcessData, applyScreeningFilters, applyBlacklist, calculateNewSubset} from "./utils";
import { saveComponentState, clearComponentState } from '../../Store/slices/appState.slice'; // Adjust the import path as necessary
import {formatLinkedSmaPortfolioConfigPayload, reverseFormattedPortfolioProperties} from "../../Configs/SmaPortfolioConfig";
import {getTickersBySymbols} from "../../Store/slices/globalResources.slice";
import {createNewPortfolio} from "../../Api/portfolioApi";
import PortfolioLink from "../../Components/Text/PortfolioLink";
import {fetchXrayData} from "../../Api/tickersApi";
import Button from "@mui/material/Button";
import {StartRounded, ContentCutRounded, FilterAltRounded, GradingRounded, SportsScoreRounded} from "@mui/icons-material";
import {SearchRounded} from "@mui/icons-material";

const SMALab = () => {
    const dispatch = useDispatch();
    const xrayData = useSelector(state => state.tickers.xrayData);
    const [consolidatedList, setConsolidatedList] = useState([]);
    const [parents, setParents] = useState(null);
    const [subset, setSubset] = useState([]);
    const [smaPortfolioTickers, setSmaPortfolioTickers] = useState([]);
    const [error, setError] = useState(null);
    const [view, setView] = useState('aggregated');
    const [etfs, setEtfs] = useState(undefined);
    const [removedByScreener, setRemovedByScreener] = useState(0);
    const [removedByBlacklist, setRemovedByBlacklist] = useState(0);
    const blacklists = useSelector(state => selectAppDataByType(state, 'blacklist'));
    const screens = useSelector(state => selectAppDataByType(state, 'screener'));
    const savedState = useSelector(state => state.appStates.componentStates['SMALab']);
    const tickers = useSelector((state) => getTickersBySymbols(state, consolidatedList?.simplifiedObjects?.map?.(x => x.symbol)) || []);


    const { smaPortfolio, config: globalConfig, updateConfig, resetSmaPortfolio } = usePortfolio();
    const [localConfig, setLocalConfig] = useState(globalConfig);


    useEffect(() => {
        /** Update the local config when the global config changes */
        setLocalConfig(globalConfig);
    }, [globalConfig]);


    useEffect(() => {
        if (smaPortfolio?.linkedPortfolio) {
            const etfsAboveZero = filterInactiveHoldings(smaPortfolio?.symbols ?? {});
            setEtfs(etfsAboveZero);
            dispatch(fetchXrayData(etfsAboveZero))
        }
        // Manage global configuration updates from smaPortfolio
        if (smaPortfolio?.params) {
            const props = reverseFormattedPortfolioProperties(smaPortfolio.params, smaPortfolio.linkedPortfolio);
            for (const [key, value] of Object.entries(props)) {
                updateConfig(key, value);
            }
        }
    }, [smaPortfolio?.linkedPortfolio, smaPortfolio?.symbols]);



    useEffect(()=>{
        if (Object.keys(xrayData).length > 0 && xrayData.hasOwnProperty('parents')) {
            const processedList = getConsolidatedStockList(xrayData.parents);
            setConsolidatedList(processedList);
            setParents(xrayData.parents);
            setError(null); // Reset error state on successful fetch
        }
    }, [xrayData])

    useEffect(() => {
        if (consolidatedList.simplifiedObjects && localConfig.cutoffPercent) {
            const newSubset = getSubset(consolidatedList.simplifiedObjects, localConfig.cutoffPercent);
            setSubset(newSubset);
        }
    }, [dispatch, localConfig.cutoffPercent])


    useEffect(() => {
        /** Rehydrate the state from the saved state if it exists */
        if (savedState) {
            [
                [setConsolidatedList, savedState.consolidatedList], [setParents, savedState.parents],
                [setSubset, savedState.subset], [setError, savedState.error],
                [setView, savedState.view], [setEtfs, savedState.etfs]].forEach(([setter, value]) => {
                    setter(value);
            });
        }
    }, [savedState]); // Dependencies adjusted as needed

    useEffect(() => {
        if (localConfig) {
            dispatch(saveComponentState({
                componentName: 'SMALab',
                componentState: { consolidatedList, parents, subset, error, view, etfs, configuration: localConfig }
            }));
        }
    }, [localConfig]); // This effect runs when localConfig changes, ensuring it has the latest data

    const handleTickersSubmit = (searchedEtfs) => {
        const newEtfs = searchedEtfs.map(x=>x.symbol);
        setEtfs(newEtfs);
        dispatch(fetchXrayData(newEtfs));
        setView('aggregated');
    };
    const handleUpdateConfig = (key, value) => {
        /** Called from AggregateToolbar to update different configuration fields with user selected values */
        updateConfig(key, value);
        setLocalConfig(prevConfig => ({ ...prevConfig, [key]: value })); // Ensure we are working with latest data
    };
    const handleCutoffSubmit = async () => {
        let newSet = calculateNewSubset(consolidatedList, localConfig.cutoffPercent);
        const selectedScreen = savedState?.configuration?.screener;
        const selectedBlacklist = savedState?.configuration?.blacklist;

        if (selectedScreen) {
            try {
                newSet = await applyScreeningFilters(selectedScreen, screens, newSet, consolidatedList, dispatch);
                setRemovedByScreener(newSet.removed);
            } catch (error) {
                setError(error.toString());
                return;
            }
        }
        if (selectedBlacklist){
            const blacklistFiltered = applyBlacklist(newSet.subset, blacklists.find(x=>x.id === selectedBlacklist)?.data);
            setRemovedByBlacklist(blacklistFiltered.removed);
            newSet.subset = blacklistFiltered.subset
        }
        setSubset(newSet);
        setView('preview');
    };

    const handleSave = ()=>{
        const smaProperties = formatLinkedSmaPortfolioConfigPayload(localConfig, smaPortfolio.linkedPortfolio);
        const formattedPortfolios = smaPortfolioTickers.map((item) => {
            const tickerObject = tickers.find(x => x.symbol === item.symbol);
            return {id: item.symbol, wgt: item.wgt, name: tickerObject?.name ?? '', type: 'stock'}
        });
        dispatch(createNewPortfolio({
            title: `${smaPortfolio.linkedPortfolio.name} - Stock SMA`,
            type: 'b',
            itemArray: formattedPortfolios,
            props: smaProperties

        }))
    }

    const handleReset = ()=>{
        dispatch(clearComponentState('SMALab'));
        resetSmaPortfolio();
        setEtfs(undefined);

    }

    let actions = [
        <Button variant={'contained'} startIcon={<SearchRounded/>} onClick={()=>setView('search')}>Search</Button>,
        <Button variant={'text'} color={'error'}  onClick={handleReset} icon={<WarningOutlined/>}>Reset</Button>
    ];
    if (view === 'preview') {
        actions.push(<Button type={'primary'} onClick={handleSave}>Save</Button>)
    }
    if (smaPortfolio && smaPortfolio.hasOwnProperty('linkedPortfolio') && smaPortfolio.linkedPortfolio.hasOwnProperty('id')) {
        actions.push(<PortfolioLink portfolio={smaPortfolio.linkedPortfolio} />);
    }
    if (localConfig.screener){
        actions.unshift(<Tag className={'danger-tag'} icon={<WarningOutlined/>}>Screen Added</Tag>)
    }
    if (localConfig.blacklist){
        actions.unshift(<Tag className={'danger-tag'} icon={<WarningOutlined/>}>Blacklist Added</Tag>)
    }

    const stepConfig = {fontSize: '16px', fontWeight: '600'};

    return (
        <div className={'white'}>
            <PageHeader title={'SMA Lab'} logoUrl={iconMapping.smalab} actions={actions}/>
            {(!etfs && Object.keys(xrayData).length===0)||view==='search'  ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', marginTop: '20px' }}>
                    <TickerSearch onTickersSubmit={handleTickersSubmit} types={['etf']} />
                </div>
            )
                :
                (
                    <div>
                            {error ? <p>Failed to load data</p> : null}
                            {view === 'aggregated' && consolidatedList.hasOwnProperty('detailedRows') ? (
                                <AggregateView detailedRows={consolidatedList.detailedRows}
                                               onSubmit={handleCutoffSubmit}
                                               blacklists={blacklists}
                                               screens={screens}
                                               consolidatedList={consolidatedList.simplifiedObjects}
                                               etfs={parents}
                                               updateConfig={handleUpdateConfig} // Passing the state update function
                                               config={localConfig}
                                />
                            ) : null}
                            {view === 'preview' ? (
                                <Preview
                                    outSet={subset.outset}
                                    subset={subset.subset}
                                    consolidatedList={consolidatedList.simplifiedObjects}
                                    setView={setView} // Pass setView to Preview
                                    config={localConfig}
                                    onSave={handleSave}
                                    updateConfig={handleUpdateConfig} // Passing the state update function
                                    setSubset={setSmaPortfolioTickers} // Pass setSubset to Preview
                                    counts={[
                                        {
                                            title: "Initial",
                                            description: <span style={{color: '#000', ...stepConfig}}>{consolidatedList.simplifiedObjects.length}</span>,
                                            icon: <StartRounded fontSize={'small'}/>,
                                            status: 'finished'
                                        },
                                        {
                                            title: "Cutoff",
                                            description: <span style={{color: '#C72323', ...stepConfig}}>x{consolidatedList.simplifiedObjects.length - (subset.subset.length + removedByScreener + removedByBlacklist )}</span>,
                                            icon: <ContentCutRounded fontSize={'small'}/>,
                                            status: 'finished'},
                                        {
                                            title: "Screen",
                                            description: <span style={{color: '#C72323', ...stepConfig}}>x {removedByScreener}</span>,
                                            icon: <FilterAltRounded fontSize={'small'}/>,
                                            status: 'finished'
                                        },
                                        {
                                            title: "Blacklist",
                                            description: <span style={{color: '#C72323', ...stepConfig}}>x {removedByBlacklist}</span>,
                                            icon: <GradingRounded fontSize={'small'}/>,
                                            status: 'finished'
                                        },
                                        {
                                            title: "Final",
                                            description: <span style={{color: '#000', ...stepConfig}}>{subset.subset.length}</span>,
                                            icon: <SportsScoreRounded sx={{color: 'green'}}/>,
                                            status: 'finished'

                                        }
                                    ]}

                                />
                            ) : null}
                    </div>

                )
            }
        </div>
    );
};


export default SMALab;